import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from "@/lib/utils"
import {
  ConfigurableProduct,
  ConfigurableProductOptions,
  ConfigurableProductOptionsValues,
  ConfigurableVariant,
  Maybe,
} from "@/types/graphql.prod"
import { isProductConfigurable } from "@/util/isProductConfigurable"
import Image from "next/image"
import { useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { getSortedColor } from "./get-sorted-color"

export interface options_available_for_selection {
  __typename: string
  option_value_uids: string[]
  attribute_code: string
}

export const StandardOptions = ({
  configurableOptions,
  variants,
  product,
  handleClick,
  optionSpecs,
  handleRefreshUrl,
  hasSizeChart,
  onShowSizeChart,
}: {
  configurableOptions: Maybe<Maybe<ConfigurableProductOptions>[]> | undefined
  variants: Maybe<Maybe<ConfigurableVariant>[]> | undefined
  product: ConfigurableProduct
  handleClick: any
  optionSpecs: string[]
  handleRefreshUrl?: any
  hasSizeChart?: any
  onShowSizeChart?: () => void
}) => {
  const form = useFormContext()
  const { watch } = form
  const allFormData = watch()

  if (!isProductConfigurable(product)) {
    return null
  }

  // configurableOptions 排序
  const sortedConfigurableOptions = configurableOptions?.sort((a, b) => {
    return (a?.position ?? 0) - (b?.position ?? 0)
  })

  const sortValues = sortedConfigurableOptions?.find(
    (item) => item?.attribute_code === "color"
  )?.values

  const sortedColor = getSortedColor({
    values: sortValues,
    variants,
    attribute_code: "color",
  })

  return (
    <div className="flex flex-col gap-4">
      {sortedConfigurableOptions?.map((option, clickIndex) => {
        if (option?.attribute_code === "color") {
          return (
            <FormField
              key={option?.uid}
              control={form.control}
              name={option?.attribute_code}
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-4 block pb-2.5 font-poppins leading-[18px] text-title">
                    <span className="font-[600]">{option?.label}</span>:{" "}
                    <span className="option_selected__KhA8p font-oswald-bold text-base capitalize">
                      {allFormData?.["color"]?.label}
                    </span>
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(value) => {
                        const selectedOption = option?.values?.find(
                          (v) => v?.uid?.toString() === value
                        )

                        if (selectedOption) {
                          field.onChange({
                            uid: value,
                            label: selectedOption?.default_label,
                            option_attribute_id: option.attribute_id,
                            option_selection_value: selectedOption?.value_index,
                          })

                          const shouldAddToSpec = optionSpecs.includes(
                            selectedOption?.value_index?.toString() ?? ""
                          )

                          if (shouldAddToSpec) {
                            handleClick({
                              bool: true,
                              text: selectedOption?.value_index?.toString(),
                              index: clickIndex,
                            })
                          } else {
                            handleClick({
                              bool: true,
                              text: "",
                              index: clickIndex,
                            })
                          }

                          handleRefreshUrl && handleRefreshUrl()
                        }
                      }}
                      value={field.value?.uid}
                      className="grid grid-cols-4 gap-x-[5px] gap-y-2.5 lg_flex lg_flex-wrap"
                    >
                      {sortedColor.map((value) => (
                        <ColorOptionRadioItem
                          key={value.uid}
                          item={value}
                          attributeCode={option?.attribute_code ?? ""}
                          variants={variants}
                          optionSpecs={optionSpecs}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage id="form-error" />
                </FormItem>
              )}
            />
          )
        }

        return (
          <FormField
            key={option?.uid}
            control={form.control}
            name={option?.attribute_code ?? ""}
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel className="text-4 block pb-2.5 font-poppins leading-[18px] text-title">
                  <div className="flex items-center">
                    <span>
                      <span className="font-[600]">
                        {option?.label === "Package Options"
                          ? "Package"
                          : option?.label}
                      </span>
                      :{" "}
                      <span className="option_selected__KhA8p font-oswald-bold text-base capitalize">
                        {allFormData?.[option?.attribute_code ?? ""]?.label}
                      </span>
                    </span>
                  </div>
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={(value) => {
                      const selectedOption = option?.values?.find(
                        (v) => v?.uid?.toString() === value
                      )

                      if (selectedOption) {
                        field.onChange({
                          uid: value,
                          label: selectedOption?.default_label,
                          option_attribute_id: option?.attribute_id,
                          option_selection_value: selectedOption?.value_index,
                        })

                        const shouldAddToSpec = optionSpecs.includes(
                          selectedOption?.value_index?.toString() ?? ""
                        )

                        if (shouldAddToSpec) {
                          handleClick({
                            bool: true,
                            text: selectedOption?.value_index?.toString(),
                            index: clickIndex,
                          })
                        } else {
                          handleClick({
                            bool: true,
                            text: "",
                            index: clickIndex,
                          })
                        }

                        // handleColorLabelChange(selectedOption?.default_label?.toLowerCase())
                      }
                    }}
                    value={field.value?.uid}
                    className="flex flex-wrap gap-[15px]"
                  >
                    {option?.values?.map((value) => (
                      <ConfigurableOptionRadioItem
                        key={value?.uid}
                        item={value}
                        isSize={option.label === "Size"}
                        optionSpecs={optionSpecs}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage id="form-error" />
              </FormItem>
            )}
          />
        )
      })}
    </div>
  )
}

const ColorOptionRadioItem = ({
  item,
  attributeCode,
  variants,
  optionSpecs,
}: {
  item: Maybe<ConfigurableProductOptionsValues>
  attributeCode: string
  variants: Maybe<Maybe<ConfigurableVariant>[]> | undefined
  optionSpecs: string[]
}) => {
  const couldSelect = optionSpecs.includes(item?.value_index?.toString() ?? "")
  const coudldNotSelect = !couldSelect

  const optionImage = useMemo(() => {
    const find = variants?.find((temp) => {
      return temp?.attributes?.some(
        (attr: any) => attr.value_index === item?.value_index
      )
    })

    if (find) {
      const swatchImage = find.product?.swatch_image

      if (swatchImage && swatchImage !== "no_selection") {
        return `/media/catalog/product/${swatchImage}`
      }

      const hover_image = find?.product?.hover_image
      const media_gallery = find?.product?.media_gallery?.sort((a, b) => {
        return (a?.position ?? 0) - (b?.position ?? 0)
      })

      const hoverImages = media_gallery?.filter((image) =>
        image?.url?.includes("hover")
      )

      if (hover_image) {
        return hover_image
      } else if (hoverImages && hoverImages?.length > 0) {
        return hoverImages?.[0]?.url
      }

      return media_gallery?.[1]?.url
    }

    return "/logo_2.jpg"
  }, [variants, item?.value_index])

  return (
    <FormItem className="space-y-0">
      <FormControl>
        <RadioGroupItem value={item?.uid ?? ""} className="peer sr-only" />
      </FormControl>
      {
        <FormLabel
          className={cn(
            "flex cursor-pointer flex-col items-center border-[2px] border-solid border-[#ddd]",
            "overflow-hidden rounded-[10px] border-[2px] hover_border-primary peer-data-[state=checked]_border-primary",
            coudldNotSelect &&
              "border-[2px] border-transparent bg-[#f8f8f8] opacity-50 peer-data-[state=checked]_border-solid peer-data-[state=checked]_border-[#333]"
          )}
        >
          {attributeCode === "color" && optionImage && (
            <Image
              src={optionImage}
              alt={item?.label ?? ""}
              width={100}
              height={100}
              unoptimized
              className="h-full w-full lg_h-[100px] lg_w-[100px]"
            />
          )}
        </FormLabel>
      }
    </FormItem>
  )
}

const ConfigurableOptionRadioItem = ({
  item,
  isSize,
  optionSpecs,
}: {
  item: Maybe<ConfigurableProductOptionsValues>
  isSize: boolean
  optionSpecs: string[]
}) => {
  const couldSelect = optionSpecs.includes(item?.value_index?.toString() ?? "")
  const coudldNotSelect = !couldSelect

  return (
    <FormItem className="flex items-center space-x-0 space-y-0">
      <FormControl>
        <RadioGroupItem value={item?.uid ?? ""} className="peer sr-only" />
      </FormControl>
      <FormLabel
        className={cn(
          "pointer-events-auto inline-flex h-[36px] min-w-[70px] cursor-pointer items-center justify-center rounded-sm border border-[2px] border-solid border-[#ddd] px-2xs py-0 text-center font-poppins text-sm font-[600] uppercase leading-none",
          "relative rounded-[10px] peer-data-[state=checked]_border-[2px] peer-data-[state=checked]_border-primary [&_has([data-state=checked])]_border-primary",
          coudldNotSelect && " bg-[rgba(0,0,0,0.2)]"
        )}
      >
        {coudldNotSelect && (
          <div className="absolute absolute h-full w-full origin-top-left rotate-[24.23deg] transform border-t-1 border-solid border-[#666]" />
        )}
        <span className={cn("text-sm lg_text-[16px] ")}>
          {isSize ? item?.swatch_data?.value || item?.label : item?.label}
        </span>
      </FormLabel>
    </FormItem>
  )
}
